import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { useConfirmationModal } from 'Hooks/useConfirmationModal';

const SELECT_MODE = 'select';
const ALL_MODE = 'all';

const BulkDeleteContext = createContext();

export const useBulkDelete = () => {
    const context = useContext(BulkDeleteContext);

    if (!context) {
        throw new Error('useBulkDelete must be used within a BulkDeleteProvider');
    }

    return context;
};

export const BulkDeleteProvider = ({ initialMode = ALL_MODE, children }) => {
    const [mode, setMode] = useState(initialMode);
    const [selectedItems, setSelectedItems] = useState(new Set());
    const [isDeleting, setIsDeleting] = useState(false);

    const confirmationModal = useConfirmationModal();

    const isSelectMode = useMemo(() => mode === SELECT_MODE, [mode]);
    const isAllMode = useMemo(() => mode === ALL_MODE, [mode]);

    const changeMode = useCallback((newMode) => {
        if (newMode !== SELECT_MODE && newMode !== ALL_MODE) {
            throw new Error(`Invalid mode: ${newMode}`);
        }

        setMode(newMode);
        deselectAll();
    }, []);

    const selectItem = useCallback((id) => {
        setSelectedItems((prev) => new Set([...prev, id]));
    }, []);

    const deselectItem = useCallback((id) => {
        setSelectedItems((prev) => {
            const newSet = new Set(prev);
            newSet.delete(id);
            return newSet;
        });
    }, []);

    const toggleItem = useCallback((id) => {
        setSelectedItems((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(id)) {
                newSet.delete(id);
            } else {
                newSet.add(id);
            }
            return newSet;
        });
    }, []);

    const selectAll = useCallback((ids) => {
        setSelectedItems(new Set(ids));
    }, []);

    const deselectAll = useCallback(() => {
        setSelectedItems(new Set());
    }, []);

    const isSelected = useCallback(
        (id) => {
            return selectedItems.has(id);
        },
        [selectedItems],
    );

    const bulkDelete = useCallback(
        async (deleteHandler, options = {}) => {
            const {
                confirmTitle = $t('Delete Selected Items'),
                confirmMessage = $t('Are you sure you want to delete the selected items?'),
                confirmWarning = $t('This action cannot be undone'),
                skipConfirmation = false,
            } = options;

            if (isSelectMode && selectedItems.size === 0) {
                return { success: false, error: $t('No items selected') };
            }

            const selectedArray = Array.from(selectedItems);

            try {
                if (!skipConfirmation) {
                    const confirmation = await confirmationModal({
                        title: confirmTitle,
                        message: confirmMessage,
                        warning: confirmWarning,
                    });

                    if (!confirmation.confirmed) {
                        return { success: false, cancelled: true };
                    }
                }

                setIsDeleting(true);

                const results = await deleteHandler(selectedArray);
                results.success = true;

                // Always clear selection on successful delete, let parent handle everything else
                deselectAll();

                return results;
            } catch (error) {
                // Let parent handle all error scenarios
                const errorResult = {
                    success: false,
                    error: error.message,
                    selectedItems: selectedArray,
                };

                return errorResult;
            } finally {
                setIsDeleting(false);
            }
        },
        [selectedItems, confirm],
    );

    const value = {
        mode,
        selectedItems: Array.from(selectedItems),
        selectedCount: selectedItems.size,
        isDeleting,
        isAllMode,
        isSelectMode,
        changeMode,
        setAllMode: () => changeMode(ALL_MODE),
        setSelectMode: () => changeMode(SELECT_MODE),
        selectItem,
        deselectItem,
        toggleItem,
        selectAll,
        deselectAll,
        isSelected,
        bulkDelete,
    };

    return <BulkDeleteContext.Provider value={value}>{children}</BulkDeleteContext.Provider>;
};

export default BulkDeleteContext;
